import React, { useState, useEffect } from 'react';
import { FEATURES } from '../config/features';

const STEPS = 3;

const ROLE_CONFIG = {
  member: {
    roleLabel: 'Member',
    step2Items: FEATURES.SCHEDULING ? [
      { icon: '📅', text: 'Meetings happen every week. Check the schedule.' },
      { icon: '🎤', text: 'Book a slot. Claim your spot on stage.' },
      { icon: '🎙', text: 'Show up. Give your speech.' },
      { icon: '💬', text: 'Get feedback from the room — and from AI.' },
    ] : [
      { icon: '📺', text: 'Watch talks from the club in the Library.' },
      { icon: '🎙', text: 'Upload a recording of your own speech.' },
      { icon: '💬', text: 'Get AI coaching on your delivery.' },
    ],
    ctaLabel: FEATURES.SCHEDULING ? 'Claim your first slot →' : 'Browse the Library →',
    ctaPath: FEATURES.SCHEDULING ? '/calendar' : '/browse',
  },
  evaluator: {
    roleLabel: 'Evaluator',
    step2Items: FEATURES.SCHEDULING ? [
      { icon: '🎤', text: 'Show up. Listen. Speak.' },
      { icon: '💬', text: 'Leave a comment on any speech you hear.' },
      { icon: '📈', text: 'The feedback sticks. The club gets better.' },
    ] : [
      { icon: '📺', text: 'Watch talks from the club in the Library.' },
      { icon: '💬', text: 'Leave a comment on any speech you hear.' },
      { icon: '📈', text: 'The feedback sticks. The club gets better.' },
    ],
    ctaLabel: FEATURES.SCHEDULING ? 'See the schedule →' : 'Browse the Library →',
    ctaPath: FEATURES.SCHEDULING ? '/calendar' : '/browse',
  },
  admin: {
    roleLabel: 'Admin',
    step2Items: [
      ...(FEATURES.SCHEDULING
        ? [
            { icon: '⚙️', text: 'Set up your club — name, meeting day, speech slots.' },
            { icon: '📅', text: 'Create meetings and invite members.' },
          ]
        : [
            { icon: '⚙️', text: 'Set up your club — name and auto-approve domain.' },
            { icon: '📺', text: 'Add videos to the Library and invite members.' },
          ]),
      { icon: '✅', text: 'Approve join requests from the Admin panel.' },
      ...(FEATURES.GAMIFICATION
        ? [{ icon: '🏆', text: 'The leaderboard and AI coaching run automatically.' }]
        : [{ icon: '🏆', text: 'AI coaching runs automatically.' }]),
    ],
    ctaLabel: 'Open Admin panel →',
    ctaPath: '/admin',
  },
};

function Waveform({ height = 20, color = 'var(--accent)' }) {
  return (
    <div className="flex items-center gap-[3px]" style={{ height }}>
      {[0.5, 0.75, 1, 0.75, 0.5].map((s, i) => (
        <div
          key={i}
          className="rounded-full"
          style={{ width: 3, height: `${s * height}px`, background: color }}
        />
      ))}
    </div>
  );
}

function ProgressDots({ current }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: STEPS }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: i === current ? 'var(--accent)' : 'var(--accent-dim)',
            transition: 'background 0.2s',
          }}
        />
      ))}
    </div>
  );
}

function BackButton({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        color: hovered ? 'var(--accent)' : 'var(--text-muted)',
        fontSize: '0.875rem',
        fontWeight: 500,
        transition: 'color 0.15s',
      }}
    >
      ← Back
    </button>
  );
}

/**
 * OnboardingModal — 3-step role-aware wizard shown once on first login.
 *
 * Props:
 *   member   { first_name, role } — used for personalization
 *   onDismiss (navigateTo?: string) => void — called with CTA path or null
 */
export default function OnboardingModal({ member, onDismiss }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onDismiss(null); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  const role = member?.role || 'member';
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.member;
  const firstName = member?.first_name || 'there';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
      >
        <ProgressDots current={step} />

        {/* Step 0 — Welcome */}
        {step === 0 && (
          <div className="space-y-4 text-center">
            <div className="flex flex-col items-center gap-3">
              <Waveform height={24} />
              <h2
                className="font-display text-2xl font-semibold"
                style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
              >
                You're in, {firstName}.
              </h2>
              <span
                className="text-xs font-medium rounded-full px-2.5 py-0.5"
                style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}
              >
                {config.roleLabel}
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              A real club. Real speeches. Real feedback. Every week.
            </p>
          </div>
        )}

        {/* Step 1 — How it works */}
        {step === 1 && (
          <div className="space-y-4">
            <h3
              className="font-display text-xl font-semibold text-center"
              style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
            >
              How it works
            </h3>
            <div className="space-y-3.5">
              {config.step2Items.map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <span className="text-lg w-7 text-center flex-shrink-0">{icon}</span>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 — First action */}
        {step === 2 && (
          <div className="space-y-3 text-center">
            <div className="flex justify-center mb-1">
              <Waveform height={20} color="var(--color-success)" />
            </div>
            <h3
              className="font-display text-2xl font-semibold"
              style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
            >
              You're ready.
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              One click. That's all it takes to start.
            </p>
          </div>
        )}

        {/* Navigation */}
        {step < STEPS - 1 ? (
          <div className={`flex items-center ${step > 0 ? 'justify-between' : 'justify-end'} pt-1`}>
            {step > 0 && <BackButton onClick={() => setStep(step - 1)} />}
            <button
              onClick={() => setStep(step + 1)}
              className="btn-bouncy py-2.5 px-5 text-sm font-semibold"
            >
              Next →
            </button>
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            <div className="flex justify-between items-center">
              <BackButton onClick={() => setStep(step - 1)} />
              <div />
            </div>
            <button
              onClick={() => onDismiss(config.ctaPath)}
              className="btn-bouncy w-full py-3 text-sm font-semibold"
            >
              {config.ctaLabel}
            </button>
            <div className="text-center">
              <button
                onClick={() => onDismiss(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: 'var(--text-faint)',
                }}
              >
                Skip for now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
